import React, { Component } from 'react';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button, Popconfirm, Radio, Spin, Table, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import { fromJS } from 'immutable';
import { get } from 'lodash';
import { meteringPointFields } from '../../constants/meteringPoint';
import { meteringPointDataSource } from '../../constants/classificators';
import {
  translateCountyClassificator,
  translateMeteringPointDataSourceClassificator,
  translateDisseminationLevelClassificator,
  translateMeteringPointStatusClassificator,
} from '../../utils/translateHelpers';
import { goToMeteringPointView } from '../../utils/gotoLink';
import { isNotRegistered } from '../../utils/meteringPointUtil';
import './MeasurementTable.css';

class MeasurementTable extends Component {
  static propTypes = {
    isAdmin: PropTypes.bool.isRequired,
    isEditMode: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    t: PropTypes.func.isRequired,
    onEditClick: PropTypes.func,
    onUnlinkClick: PropTypes.func,
    isAdminEdit: PropTypes.bool,
    checkEligibility: PropTypes.bool,
    showProductionColumns: PropTypes.bool,
    productionUnitId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    data: PropTypes.array,
  };

  static defaultProps = {
    isAdminEdit: false,
    checkEligibility: false,
    showProductionColumns: false,
  };

  getColumns = () => {
    const {
      t,
      data,
      isEditMode,
      isAdminEdit,
      isAdmin,
      checkEligibility,
      showProductionColumns,
      onEditClick,
      onUnlinkClick,
    } = this.props;

    const columns =
      isEditMode && !isAdminEdit
        ? []
        : [
            {
              title: '',
              dataIndex: meteringPointFields.ID,
              render: (value, values) => {
                const alreadyUsed =
                  get(values, meteringPointFields.IS_ALREADY_USED) === true;
                // Only active points with a dissemination level may be linked. A point AVP knows
                // but BMR does not has neither a details view nor a dissemination level yet, so it
                // stays selectable - the inline form collects the level while creating the point.
                const notEligible =
                  checkEligibility &&
                  (get(values, meteringPointFields.STATUS) !== 'ACTIVE' ||
                    (!get(values, meteringPointFields.DISSEMINATION_LEVEL) &&
                      !isNotRegistered(get(values, meteringPointFields.ID))));
                const radio = (
                  <Radio
                    disabled={alreadyUsed || notEligible}
                    value={
                      get(values, meteringPointFields.ID)
                        ? get(values, meteringPointFields.ID)
                        : get(values, meteringPointFields.EICZ_CODE)
                    }
                  />
                );
                return notEligible ? (
                  <Tooltip title={t('measurement.addExistingDisabledTooltip')}>
                    <span>{radio}</span>
                  </Tooltip>
                ) : (
                  radio
                );
              },
            },
          ];

    columns.push(
      {
        title: t('measurement.type'),
        dataIndex: meteringPointFields.DATA_SOURCE,
        render: translateMeteringPointDataSourceClassificator,
      },
      {
        title: t('measurement.eiczCodeLabel'),
        dataIndex: meteringPointFields.EICZ_CODE,
      }
    );

    const shouldRenderActualOwnerEicx =
      data.some(
        (row) =>
          Boolean(get(row, meteringPointFields.ACTUAL_OWNER_EICX)) &&
          String(get(row, meteringPointFields.LEGAL_ENTITY).eicxCode) !==
            String(get(row, meteringPointFields.ACTUAL_OWNER_EICX))
      ) && isAdmin;

    if (shouldRenderActualOwnerEicx) {
      columns.push({
        title: t('measurement.actualOwnerEicx'),
        dataIndex: meteringPointFields.ACTUAL_OWNER_EICX,
      });
    }

    if (showProductionColumns) {
      columns.push({
        title: t('measurement.headerDisseminationLevel'),
        dataIndex: meteringPointFields.DISSEMINATION_LEVEL,
        render: translateDisseminationLevelClassificator,
      });
    }

    columns.push(
      {
        title: t('measurement.headerCountry'),
        dataIndex: meteringPointFields.COUNTY,
        render: translateCountyClassificator,
      },
      {
        title: t('measurement.headerCity'),
        dataIndex: meteringPointFields.CITY,
      },
      {
        title: t('measurement.headerDistrict'),
        dataIndex: meteringPointFields.DISTRICT,
      },
      {
        title: t('measurement.headerAddress'),
        dataIndex: meteringPointFields.ADDRESS,
      }
    );

    if (showProductionColumns) {
      columns.push({
        title: t('measurement.headerStatus'),
        dataIndex: meteringPointFields.STATUS,
        render: translateMeteringPointStatusClassificator,
      });
    }

    columns.push({
      title: t('measurement.actions'),
      dataIndex: meteringPointFields.ID,
      key: 'actions',
      render: (value, values) => (
        <div>
          {onEditClick
            ? (!isEditMode || isAdminEdit) &&
              get(values, meteringPointFields.DATA_SOURCE) ===
                meteringPointDataSource.VIRTUAL && (
                <Button
                  shape="circle"
                  icon={<EditOutlined />}
                  size="small"
                  title={t('measurement.edit')}
                  type="primary"
                  onClick={() => onEditClick(fromJS(values))}
                />
              )
            : isAdmin &&
              !isNotRegistered(value) && (
                <Button
                  shape="circle"
                  icon={<EditOutlined />}
                  size="small"
                  title={t('measurement.view')}
                  type="primary"
                  onClick={() =>
                    goToMeteringPointView(
                      value,
                      null,
                      this.props.productionUnitId
                    )
                  }
                />
              )}
          {onUnlinkClick &&
            isAdmin &&
            !isNotRegistered(value) &&
            (data.length <= 1 ? (
              <Tooltip title={t('measurement.unlinkDisabledLastPoint')}>
                <Button
                  shape="circle"
                  icon={<DeleteOutlined />}
                  size="small"
                  type="primary"
                  danger
                  disabled
                  style={{ marginLeft: 4 }}
                />
              </Tooltip>
            ) : (
              <Popconfirm
                title={t('measurement.unlinkConfirmTitle')}
                onConfirm={() => onUnlinkClick(value)}
                okText={t('measurement.unlinkConfirmOk')}
                cancelText={t('measurement.unlinkConfirmCancel')}
              >
                <Tooltip title={t('measurement.unlinkTooltip')}>
                  <Button
                    shape="circle"
                    icon={<DeleteOutlined />}
                    size="small"
                    type="primary"
                    danger
                    style={{ marginLeft: 4 }}
                  />
                </Tooltip>
              </Popconfirm>
            ))}
        </div>
      ),
    });

    return columns;
  };

  renderTable = () => {
    const { data, t } = this.props;

    return (
      <Table
        rowKey={(record) =>
          record[meteringPointFields.ID] ||
          record[meteringPointFields.EICZ_CODE]
        }
        columns={this.getColumns(t, data)}
        dataSource={data}
        bordered
      />
    );
  };

  render() {
    const {
      onSelectMeasurement,
      selectedMeasurementId,
      isLoading,
      isEditMode,
      isAdminEdit,
    } = this.props;

    return (
      <Spin spinning={isLoading}>
        {isEditMode && !isAdminEdit ? (
          this.renderTable()
        ) : (
          <Radio.Group
            className="component-measurement-table"
            onChange={(e) => {
              onSelectMeasurement(e.target.value);
            }}
            value={selectedMeasurementId}
          >
            {this.renderTable()}
          </Radio.Group>
        )}
      </Spin>
    );
  }
}

export default MeasurementTable;
